import { inject } from 'tsyringe'
import { containerScoped } from '~/decorators/dependency-container'
import { Store } from 'vuex'
import { RootState } from '~/store/types'
import LoggerService from '~/services/LoggerService'

@containerScoped()
export class CanvasService {
  constructor(
    @inject(Store) private store: Store<RootState>,
    @inject(LoggerService) private logger: LoggerService
  ) {}

  getCroppedImage(
    origFile: any,
    x: number,
    y: number,
    zoom: number,
    appliedWidth: number,
    appliedHeight: number,
    baseWidth: number,
    baseHeight: number,
    bgColor: string
  ): Promise<any> {
    const file = origFile.file

    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = e => {
        if (file.type.match('image.*') && e.target) {
          const origImg = new Image()
          origImg.onload = event => {
            // @ts-ignore
            const ewidth = Math.max(event.target.width, baseWidth)
            // @ts-ignore
            const eheight = Math.max(event.target.height, baseHeight)

            const squareMin = Math.min(ewidth, eheight)
            const canvas = document.createElement('canvas')

            // canvas settings
            canvas.width = squareMin
            canvas.height = squareMin
            canvas.style.opacity = '0'

            try {
              // some browsers dont support canvas, if something here throws an error, we don't compress
              const ctx = canvas.getContext('2d')
              // @ts-ignore
              ctx.fillStyle = bgColor
              // @ts-ignore
              ctx.fillRect(0, 0, canvas.width, canvas.height)
              const sizeRatioW = ewidth / appliedWidth
              const sizeRatioH = eheight / appliedHeight

              const descreasePercentage = Math.abs(
                (appliedWidth - baseWidth) / baseWidth
              )

              let zoomToUse = descreasePercentage + 1

              let xOffset = canvas.width / 2 + x * sizeRatioW * zoomToUse
              let yOffset = canvas.height / 2 + y * sizeRatioH * zoomToUse
              if (zoom < 0) {
                // calculate the percentage decrease from  appliedwidth to basewidth?

                zoomToUse = 1 - descreasePercentage
                xOffset = canvas.width / 2 - x * sizeRatioW * zoomToUse
                yOffset = canvas.height / 2 - y * sizeRatioH * zoomToUse
              }

              // @ts-ignore
              ctx.setTransform(zoomToUse, 0, 0, zoomToUse, xOffset, yOffset)
              // @ts-ignore
              ctx.drawImage(origImg, -origImg.width / 2, -origImg.height / 2) // draw the image offset by half

              const resultFile = this.base64ToFile(
                canvas.toDataURL('image/jpeg'),
                file
              )

              const resultSrc = canvas.toDataURL('image/jpeg')

              const result = { file: resultFile, src: resultSrc }

              resolve(result)
            } catch (e) {
              this.logger.captureError(new TypeError(`Error cropping`))
              this.logger.captureError(e)
              reject(e)
            }
          }
          // @ts-ignore
          origImg.src = e.target.result
        } else {
          this.logger.captureError(new TypeError(`File not an image`))
          reject(new TypeError(`File not an image`))
        }
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  base64ToFile(dataURI: any, origFile: any) {
    let byteString

    if (dataURI.split(',')[0].includes('base64')) {
      byteString = atob(dataURI.split(',')[1])
    } else {
      byteString = decodeURI(dataURI.split(',')[1])
    }

    const mimestring = dataURI
      .split(',')[0]
      .split(':')[1]
      .split(';')[0]

    const content = []
    for (let i = 0; i < byteString.length; i++) {
      content[i] = byteString.charCodeAt(i)
    }

    const newFile = new File([new Uint8Array(content)], origFile.name, {
      type: mimestring
    })

    return newFile
  }

  getAverageColorsFromImage(imgEl: any) {
    const blockSize = 5 // only visit every 5 pixels
    const defaultRGB = { r: 255, g: 255, b: 255 } // for non-supporting envs - white
    const canvas = document.createElement('canvas')
    const context = canvas.getContext && canvas.getContext('2d')
    let data
    let i = -4
    const rgb = { r: 0, g: 0, b: 0 }
    let count = 0

    if (!context) {
      return defaultRGB
    }

    const height = (canvas.height =
      imgEl.naturalHeight || imgEl.offsetHeight || imgEl.height)
    const width = (canvas.width =
      imgEl.naturalWidth || imgEl.offsetWidth || imgEl.width)

    context.drawImage(imgEl, 0, 0)

    try {
      data = context.getImageData(0, 0, width, height)
    } catch (e) {
      /* security error, img on diff domain */
      return defaultRGB
    }

    const length = data.data.length

    while ((i += blockSize * 4) < length) {
      ++count
      rgb.r += data.data[i]
      rgb.g += data.data[i + 1]
      rgb.b += data.data[i + 2]
    }

    // ~~ used to floor values
    rgb.r = ~~(rgb.r / count)
    rgb.g = ~~(rgb.g / count)
    rgb.b = ~~(rgb.b / count)

    function rgbToHex(r: number, g: number, b: number) {
      function componentToHex(v: any) {
        const hex = v.toString(16)
        return hex.length === 1 ? '0' + hex : hex
      }
      return '#' + componentToHex(r) + componentToHex(g) + componentToHex(b)
    }

    return rgbToHex(rgb.r, rgb.g, rgb.b)
  }
}
